import React, { useState, useEffect, useCallback } from 'react';
import { shiftRequestsAPI, medicsAPI, assignmentAPI, WARNING_TYPES, WARNING_ACTIONS } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Check, 
  X, 
  Clock, 
  CalendarDays,
  Users,
  AlertTriangle,
  Loader2,
  ShieldAlert,
  FileWarning,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, startOfMonth, endOfMonth, addMonths } from 'date-fns';

const STATUS_STYLES = {
  requested: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20',
  approved: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  declined: 'bg-red-500/15 text-red-400 border-red-500/20',
};

const COMPLIANCE_STYLES = {
  compliant: 'bg-[#1ABC9C]/15 text-[#1ABC9C]',
  expiring_soon: 'bg-[#FFC04D]/15 text-[#FFC04D]',
  expired: 'bg-red-500/15 text-red-400',
  pending: 'bg-gray-500/15 text-gray-400',
};

export default function ShiftRequests() {
  const [requests, setRequests] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [medics, setMedics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('requested');
  const [processingId, setProcessingId] = useState(null);
  const [selectedMedicId, setSelectedMedicId] = useState('all');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [conflictWarning, setConflictWarning] = useState(null);
  
  // Compliance warning state
  const [complianceWarning, setComplianceWarning] = useState(null);
  const [complianceAcknowledged, setComplianceAcknowledged] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(addMonths(currentMonth, 1)), 'yyyy-MM-dd');
      
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const availParams = { start_date: startDate, end_date: endDate };
      if (selectedMedicId !== 'all') {
        availParams.medic_id = selectedMedicId;
      }

      const [requestsRes, availRes, medicsRes] = await Promise.all([
        shiftRequestsAPI.list(params),
        shiftRequestsAPI.getMedicAvailability(availParams),
        medicsAPI.list(),
      ]);

      setRequests(requestsRes.data);
      setAvailability(availRes.data);
      setMedics(medicsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, currentMonth, selectedMedicId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Log compliance warning action to audit
  const logComplianceWarningAction = async (warningType, action, medicId, shiftId) => {
    try {
      await assignmentAPI.logWarningAction({
        warning_type: warningType,
        action: action,
        medic_id: medicId,
        shift_id: shiftId,
      });
    } catch (error) {
      console.error('Failed to log compliance warning action:', error);
    }
  };

  const handleApprove = async (requestId, force = false, skipComplianceCheck = false) => {
    const request = requests.find(r => r.id === requestId);
    if (!request) return;
    
    // Check compliance status first (unless skipping)
    if (!skipComplianceCheck && !force) {
      const complianceStatus = request.medic_compliance_status;
      
      if (complianceStatus === 'expired' || complianceStatus === 'expiring_soon') {
        // Log that warning was viewed
        await logComplianceWarningAction(
          complianceStatus === 'expired' ? WARNING_TYPES.EXPIRED : WARNING_TYPES.EXPIRING_SOON,
          WARNING_ACTIONS.VIEWED,
          request.medic_id,
          request.shift_id
        );
        
        setComplianceWarning({
          requestId,
          medicName: request.medic_name,
          medicId: request.medic_id,
          shiftId: request.shift_id,
          shiftDate: request.shift_date,
          complianceStatus,
          hasConflicts: request.has_conflicts,
          conflicts: request.conflicts
        });
        setComplianceAcknowledged(false);
        return;
      }
    }
    
    setProcessingId(requestId);
    try {
      await shiftRequestsAPI.approve(requestId, force);
      toast.success('Request approved and medic assigned to shift');
      setConflictWarning(null);
      setComplianceWarning(null);
      fetchData();
    } catch (error) {
      const errData = error.response?.data?.detail;
      
      // Check if this is a conflict error
      if (errData && errData.error === 'SHIFT_CONFLICT') {
        const request = requests.find(r => r.id === requestId);
        setConflictWarning({
          requestId,
          message: errData.message,
          conflicts: errData.conflicts,
          summary: errData.conflict_summary,
          medicName: request?.medic_name || 'Medic',
          shiftDate: request?.shift_date
        });
      } else {
        toast.error(typeof errData === 'string' ? errData : 'Failed to approve request');
      }
    } finally {
      setProcessingId(null);
    }
  };

  const handleComplianceAcknowledgedApprove = async () => {
    if (!complianceWarning || !complianceAcknowledged) return;
    
    // Log acknowledgement
    await logComplianceWarningAction(
      complianceWarning.complianceStatus === 'expired' ? WARNING_TYPES.EXPIRED : WARNING_TYPES.EXPIRING_SOON,
      WARNING_ACTIONS.ACKNOWLEDGED,
      complianceWarning.medicId,
      complianceWarning.shiftId
    );
    
    // Proceed with approval (skip compliance check since we just acknowledged)
    await handleApprove(complianceWarning.requestId, false, true);
    setComplianceWarning(null);
    setComplianceAcknowledged(false);
  };

  const handleCancelComplianceWarning = async () => {
    if (complianceWarning) {
      await logComplianceWarningAction(
        complianceWarning.complianceStatus === 'expired' ? WARNING_TYPES.EXPIRED : WARNING_TYPES.EXPIRING_SOON,
        WARNING_ACTIONS.CANCELLED,
        complianceWarning.medicId,
        complianceWarning.shiftId
      );
    }
    setComplianceWarning(null);
    setComplianceAcknowledged(false);
  };

  const handleForceApprove = async () => {
    if (conflictWarning?.requestId) {
      await handleApprove(conflictWarning.requestId, true);
    }
  };

  const handleDecline = async (requestId) => {
    setProcessingId(requestId);
    try {
      await shiftRequestsAPI.decline(requestId);
      toast.success('Request declined');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline request');
    } finally {
      setProcessingId(null);
    }
  };

  // Group availability by date for calendar
  const getAvailabilityByDate = () => {
    const byDate = {};
    availability.forEach(a => {
      if (!byDate[a.date]) {
        byDate[a.date] = [];
      }
      byDate[a.date].push(a);
    });
    return byDate;
  };

  const availabilityByDate = getAvailabilityByDate();
  
  // Get modifiers for calendar
  const getCalendarModifiers = () => {
    const available = [];
    const unavailable = [];
    const mixed = [];
    
    Object.entries(availabilityByDate).forEach(([date, entries]) => {
      const parsedDate = parseISO(date);
      const hasAvailable = entries.some(e => e.available);
      const hasUnavailable = entries.some(e => !e.available);
      
      if (hasAvailable && hasUnavailable) {
        mixed.push(parsedDate);
      } else if (hasAvailable) {
        available.push(parsedDate);
      } else {
        unavailable.push(parsedDate);
      }
    });

    return { available, unavailable, mixed };
  };

  const calendarModifiers = getCalendarModifiers();

  const pendingCount = requests.filter(r => r.status === 'requested').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shift-requests-container">
      <Tabs defaultValue="requests" className="w-full">
        <TabsList className="bg-[#2B2B2B] border-white/5">
          <TabsTrigger 
            value="requests" 
            className="data-[state=active]:bg-[#1ABC9C]/20 data-[state=active]:text-[#1ABC9C]"
            data-testid="requests-tab"
          >
            Shift Requests {pendingCount > 0 && `(${pendingCount})`}
          </TabsTrigger>
          <TabsTrigger 
            value="availability" 
            className="data-[state=active]:bg-[#1ABC9C]/20 data-[state=active]:text-[#1ABC9C]"
            data-testid="availability-tab"
          >
            Medic Availability
          </TabsTrigger>
        </TabsList>

        {/* Shift Requests Tab */}
        <TabsContent value="requests" className="mt-6 space-y-4">
          <div className="flex items-center gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-[#2B2B2B] border-white/10">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-[#2B2B2B] border-white/10">
                <SelectItem value="all">All Requests</SelectItem>
                <SelectItem value="requested">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {requests.length === 0 ? (
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardContent className="p-8 text-center">
                <Clock className="w-12 h-12 mx-auto mb-3 text-[#A1A1AA] opacity-50" />
                <p className="text-[#A1A1AA]">No shift requests found</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-[#A1A1AA]">Medic</TableHead>
                      <TableHead className="text-[#A1A1AA]">Compliance</TableHead>
                      <TableHead className="text-[#A1A1AA]">Conflicts</TableHead>
                      <TableHead className="text-[#A1A1AA]">Shift Date</TableHead>
                      <TableHead className="text-[#A1A1AA]">Time</TableHead>
                      <TableHead className="text-[#A1A1AA]">Location</TableHead>
                      <TableHead className="text-[#A1A1AA]">Status</TableHead>
                      <TableHead className="text-[#A1A1AA]">Requested</TableHead>
                      <TableHead className="text-[#A1A1AA] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow 
                        key={request.id} 
                        className={`border-white/5 hover:bg-white/5 ${request.has_conflicts ? 'bg-red-500/5' : ''}`}
                        data-testid={`request-row-${request.id}`}
                      >
                        <TableCell className="text-[#F2F2F2] font-medium">
                          {request.medic_name || 'Unknown'}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${COMPLIANCE_STYLES[request.medic_compliance_status] || COMPLIANCE_STYLES.pending} border-0`}>
                            {request.medic_compliance_status === 'expiring_soon' ? (
                              <>
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Expiring
                              </>
                            ) : (
                              request.medic_compliance_status || 'pending'
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {request.has_conflicts ? (
                            <Badge className="bg-red-500/15 text-red-400 border-0">
                              <ShieldAlert className="w-3 h-3 mr-1" />
                              {request.conflicts?.length || 0} conflict(s)
                            </Badge>
                          ) : (
                            <Badge className="bg-[#1ABC9C]/15 text-[#1ABC9C] border-0">
                              <Check className="w-3 h-3 mr-1" />
                              None
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-[#F2F2F2]">
                          {request.shift_date ? format(new Date(request.shift_date), 'MMM dd, yyyy') : 'N/A'}
                        </TableCell>
                        <TableCell className="text-[#A1A1AA] font-['JetBrains_Mono'] text-sm">
                          {request.shift_start_time} - {request.shift_end_time}
                        </TableCell>
                        <TableCell className="text-[#F2F2F2]">
                          {request.shift_location || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_STYLES[request.status]}>
                            {request.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[#A1A1AA] text-sm">
                          {format(new Date(request.created_at), 'MMM dd, HH:mm')}
                        </TableCell>
                        <TableCell className="text-right">
                          {request.status === 'requested' && (
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                className={request.has_conflicts ? "bg-orange-500 hover:bg-orange-500/90" : "bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"}
                                onClick={() => handleApprove(request.id)}
                                disabled={processingId === request.id}
                                data-testid={`approve-${request.id}`}
                              >
                                {processingId === request.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Check className="w-4 h-4 mr-1" />
                                    Approve
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDecline(request.id)}
                                disabled={processingId === request.id}
                                data-testid={`decline-${request.id}`}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Decline
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Medic Availability Tab */}
        <TabsContent value="availability" className="mt-6 space-y-4">
          <div className="flex items-center gap-4">
            <Select value={selectedMedicId} onValueChange={setSelectedMedicId}>
              <SelectTrigger className="w-[200px] bg-[#2B2B2B] border-white/10">
                <SelectValue placeholder="Filter by medic" />
              </SelectTrigger>
              <SelectContent className="bg-[#2B2B2B] border-white/10">
                <SelectItem value="all">All Medics</SelectItem>
                {medics.map(medic => (
                  <SelectItem key={medic.id} value={medic.id}>
                    {medic.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Calendar View */}
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardHeader>
                <CardTitle className="text-[#F2F2F2] font-['Manrope'] flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-[#1ABC9C]" />
                  Availability Calendar
                </CardTitle>
              </CardHeader>
              <CardContent className="flex justify-center">
                <Calendar
                  mode="single"
                  month={currentMonth}
                  onMonthChange={setCurrentMonth}
                  className="rounded-md border border-white/10 bg-[#1A1A1A] p-3"
                  modifiers={calendarModifiers}
                  modifiersStyles={{
                    available: {
                      backgroundColor: 'rgba(26, 188, 156, 0.2)',
                      color: '#1ABC9C',
                      borderRadius: '4px',
                    },
                    unavailable: {
                      backgroundColor: 'rgba(239, 68, 68, 0.2)',
                      color: '#ef4444',
                      borderRadius: '4px',
                    },
                    mixed: {
                      backgroundColor: 'rgba(255, 192, 77, 0.2)',
                      color: '#FFC04D',
                      borderRadius: '4px',
                    },
                  }}
                  data-testid="availability-calendar-view"
                />
              </CardContent>
            </Card>

            {/* Availability List */}
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardHeader>
                <CardTitle className="text-[#F2F2F2] font-['Manrope'] flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#1ABC9C]" />
                  Availability Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Legend */}
                <div className="flex flex-wrap gap-4 mb-4 p-3 bg-[#1A1A1A] rounded-lg border border-white/5">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-[#1ABC9C]/20 border border-[#1ABC9C]/30" />
                    <span className="text-sm text-[#A1A1AA]">Available</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-red-500/20 border border-red-500/30" />
                    <span className="text-sm text-[#A1A1AA]">Unavailable</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-[#FFC04D]/20 border border-[#FFC04D]/30" />
                    <span className="text-sm text-[#A1A1AA]">Mixed</span>
                  </div>
                </div>

                {availability.length === 0 ? (
                  <div className="text-center py-8 text-[#A1A1AA]">
                    <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No availability data for selected period</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {availability.slice(0, 50).map((entry) => (
                      <div 
                        key={entry.id}
                        className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-lg border border-white/5"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${entry.available ? 'bg-[#1ABC9C]' : 'bg-red-500'}`} />
                          <div>
                            <p className="text-sm font-medium text-[#F2F2F2]">{entry.medic_name}</p>
                            <p className="text-xs text-[#A1A1AA]">
                              {format(new Date(entry.date), 'EEE, MMM dd')}
                            </p>
                          </div>
                        </div>
                        <Badge className={entry.available ? 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-0' : 'bg-red-500/15 text-red-400 border-0'}>
                          {entry.available ? 'Available' : 'Unavailable'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Conflict Warning Dialog */}
      <Dialog open={!!conflictWarning} onOpenChange={() => setConflictWarning(null)}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <ShieldAlert className="w-5 h-5" />
              Shift Conflict Detected
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {conflictWarning?.message}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm font-medium text-red-400 mb-2">
                {conflictWarning?.medicName} has conflicting shifts on {conflictWarning?.shiftDate}:
              </p>
              <ul className="space-y-1">
                {conflictWarning?.conflicts?.map((c, i) => (
                  <li key={i} className="text-sm text-[#A1A1AA] flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    {c.start_time} - {c.end_time} at {c.location}
                  </li>
                ))}
              </ul>
            </div>
            
            <p className="text-sm text-[#A1A1AA]">
              Approving this request will result in double-booking the medic. 
              Are you sure you want to proceed?
            </p>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setConflictWarning(null)}
              className="border-white/10 text-[#F2F2F2] hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleForceApprove}
              disabled={processingId}
              data-testid="force-approve-btn"
            >
              {processingId ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <AlertTriangle className="w-4 h-4 mr-2" />
              )}
              Approve Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compliance Warning Dialog */}
      <Dialog open={!!complianceWarning} onOpenChange={() => handleCancelComplianceWarning()}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2] max-w-md">
          <DialogHeader>
            <div className={`mx-auto p-4 rounded-full mb-4 ${
              complianceWarning?.complianceStatus === 'expired' 
                ? 'bg-red-500/10 border border-red-500/20' 
                : 'bg-[#FFC04D]/10 border border-[#FFC04D]/20'
            }`}>
              {complianceWarning?.complianceStatus === 'expired' ? (
                <XCircle className="w-12 h-12 text-red-400" />
              ) : (
                <FileWarning className="w-12 h-12 text-[#FFC04D]" />
              )}
            </div>
            <DialogTitle className="text-center text-xl">
              {complianceWarning?.complianceStatus === 'expired' 
                ? 'Medic Not Compliant'
                : 'Documents Expiring Soon'
              }
            </DialogTitle>
            <DialogDescription className="text-center text-[#A1A1AA]">
              Review compliance status before approving this shift request
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="text-center">
              <span className="text-[#A1A1AA]">Medic: </span>
              <span className="text-[#F2F2F2] font-medium">{complianceWarning?.medicName}</span>
            </div>
            
            <div className="text-center">
              <span className="text-[#A1A1AA]">Shift Date: </span>
              <span className="text-[#F2F2F2] font-medium">
                {complianceWarning?.shiftDate ? format(new Date(complianceWarning.shiftDate), 'MMM dd, yyyy') : 'N/A'}
              </span>
            </div>
            
            <div className={`p-4 rounded-lg border ${
              complianceWarning?.complianceStatus === 'expired'
                ? 'border-red-500/20 bg-red-500/5'
                : 'border-[#FFC04D]/20 bg-[#FFC04D]/5'
            }`}>
              <p className={`text-sm ${
                complianceWarning?.complianceStatus === 'expired'
                  ? 'text-red-300'
                  : 'text-[#FFC04D]'
              }`}>
                {complianceWarning?.complianceStatus === 'expired'
                  ? 'This medic has one or more expired compliance documents. Please ensure they provide updated documentation. You may continue with approval after acknowledging this warning.'
                  : 'This medic has compliance documents that will expire within the next 30 days. Please ensure updated documents are requested.'}
              </p>
            </div>

            {complianceWarning?.hasConflicts && (
              <div className="p-4 rounded-lg border border-red-500/20 bg-red-500/5">
                <div className="flex items-center gap-2 text-red-400 text-sm font-medium mb-2">
                  <ShieldAlert className="w-4 h-4" />
                  Additionally: Shift Conflicts Detected
                </div>
                <p className="text-xs text-[#A1A1AA]">
                  This medic also has {complianceWarning.conflicts?.length || 0} conflicting shift(s).
                  A separate warning will appear after this one.
                </p>
              </div>
            )}
            
            <div className="flex items-start gap-3 p-3 bg-[#1A1A1A] rounded-lg border border-white/5">
              <Checkbox 
                id="compliance-acknowledge" 
                checked={complianceAcknowledged}
                onCheckedChange={setComplianceAcknowledged}
                className="mt-0.5 border-white/20 data-[state=checked]:bg-[#1ABC9C] data-[state=checked]:border-[#1ABC9C]"
              />
              <label 
                htmlFor="compliance-acknowledge" 
                className="text-sm text-[#A1A1AA] cursor-pointer leading-relaxed"
              >
                I acknowledge the compliance issues and confirm I want to proceed with this shift assignment.
              </label>
            </div>
          </div>

          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button 
              variant="ghost" 
              onClick={handleCancelComplianceWarning}
              className="text-[#A1A1AA] flex-1"
              data-testid="compliance-warning-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleComplianceAcknowledgedApprove}
              disabled={!complianceAcknowledged || processingId}
              className={`flex-1 ${
                complianceWarning?.complianceStatus === 'expired'
                  ? 'bg-red-500 hover:bg-red-600'
                  : 'bg-[#FFC04D] hover:bg-[#FFC04D]/90 text-black'
              }`}
              data-testid="compliance-warning-confirm-btn"
            >
              {processingId ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Acknowledge & Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
